import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ExternalLink, TrendingUp, TrendingDown, Users, FileText, BarChart3 } from 'lucide-react';
import { getMarketStatus } from './marketutils';
import { formatPrice, formatSmartNumber } from './formatters';
import SentimentAggregatorChip from './SentimentAggregatorChip';
import StockLogo from '../../../components/StockLogo/StockLogoRender';

const StockHeader = ({ data, marketStatus, openCloseData }) => {
  const { t } = useTranslation();
  const statusInfo = getMarketStatus(marketStatus, t);
  const isUp = data.change != null ? data.change >= 0 : null;

  return (
    <div className="bg-white border border-gray-200 dark:bg-slate-900 dark:border-slate-700 rounded-2xl p-6 shadow-xl">
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
        {/* Left Section - Company Info */}
        <div className="space-y-4 flex-1">
          {/* Main Header Row */}
          <div className="flex items-center gap-4 flex-wrap">
            <StockLogo symbol={data.symbol} name={data.companyName} size="w-14 h-14" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 dark:from-violet-400 dark:via-indigo-300 dark:to-blue-400 bg-clip-text text-transparent">
              {data.symbol}
            </h1>

            {/* IPO Badge */}
            {data.listDate && (
              <span className="px-3 py-1.5 rounded-full text-sm font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 dark:bg-indigo-500/20 dark:border-indigo-400/30 dark:text-indigo-300">
                {t('stocks.header.ipoBadge', { year: new Date(data.listDate).getFullYear() })}
              </span>
            )}

            {/* Market Status */}
            <span className={`px-4 py-2 text-sm font-semibold rounded-full border flex items-center gap-2 ${statusInfo.bgColor} ${statusInfo.color} transition-all duration-300 hover:scale-105`}>
              <statusInfo.icon className="w-4 h-4" />
              {statusInfo.label}
            </span>

            {/* Server Time */}
            {marketStatus?.serverTime && (
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 border border-gray-300 dark:text-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-full px-3 py-2">
                <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                {new Date(marketStatus.serverTime).toLocaleString('en-US', {
                  timeStyle: 'short',
                  dateStyle: 'short'
                })}
              </div>
            )}
          </div>

          {/* Company Name & Links Row */}
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              {data.companyName}
            </h2>

            {data.homepage && (
              <a
                href={data.homepage}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-500/20 transition-all duration-300"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Metrics Row */}
          <div className="flex items-center gap-4 flex-wrap">
            {data.marketCap && (
              <div className="flex items-center gap-2 bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-full px-3 py-2">
                <BarChart3 className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('stocks.header.capPrefix')} <span className="text-emerald-700 dark:text-emerald-400 font-bold">{formatSmartNumber(data.marketCap)}</span>
                </span>
              </div>
            )}

            {data.volume && (
              <div className="flex items-center gap-2 bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-full px-3 py-2">
                <Users className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('stocks.header.volPrefix')} <span className="text-amber-700 dark:text-amber-400 font-bold">{formatSmartNumber(data.volume)}</span>
                </span>
              </div>
            )}

            {data.avgVolume && (
              <div className="flex items-center gap-2 bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-full px-3 py-2">
                <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('stocks.header.avgPrefix')} <span className="text-purple-600 dark:text-purple-400 font-bold">{formatSmartNumber(data.avgVolume)}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Price Display -- was text-right unconditionally
            and text-5xl at every width, which on mobile (where this section
            stacks below the left info block instead of sitting beside it)
            put a huge right-aligned price under a left-aligned block,
            reading as disconnected/oversized rather than part of the same
            header. Left-aligned until xl (where it's a genuine side-by-side
            layout again) and scaled down a step. */}
        <div className="text-left xl:text-right">
          {/* Current Price */}
          <div className="text-3xl sm:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-white mb-3">
            {formatPrice(data.currentPrice)}
          </div>

          {/* Change Indicator */}
          {data.change != null && data.changePercent != null && (
            <div className={`inline-flex items-center gap-4 px-5 py-3 rounded-xl border transition-all duration-300 hover:scale-105 ${
              isUp
                ? 'bg-gradient-to-r from-green-50 to-green-100 text-green-700 border-green-300 dark:from-green-900/40 dark:to-green-800/30 dark:text-green-400 dark:border-green-700/60 shadow-lg shadow-green-500/10'
                : 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-300 dark:from-red-900/40 dark:to-red-800/30 dark:text-red-400 dark:border-red-700/60 shadow-lg shadow-red-500/10'
            }`}>
              {isUp ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              <div className="space-y-1">
                <div className="text-lg font-bold">
                  {isUp ? '+' : ''}{data.change.toFixed(2)}
                </div>
                <div className="text-sm font-semibold">
                  ({isUp ? '+' : ''}{data.changePercent.toFixed(2)}%)
                </div>
              </div>
            </div>
          )}

          {/* Price Ranges */}
          <div className="mt-4 space-y-2 text-sm">
            {data.dayHigh && data.dayLow && (
              <div className="bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">{t('stocks.header.dayRangePrefix')} </span>
                <span className="text-red-600 dark:text-red-400 font-bold">{formatPrice(data.dayLow)}</span>
                <span className="text-gray-500 dark:text-gray-400 mx-2">-</span>
                <span className="text-green-700 dark:text-green-400 font-bold">{formatPrice(data.dayHigh)}</span>
              </div>
            )}

            {data.fiftyTwoWeekHigh && data.fiftyTwoWeekLow && (
              <div className="bg-gray-100 border border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 rounded-lg px-3 py-2">
                <span className="text-gray-500 dark:text-gray-400">{t('stocks.header.week52Prefix')} </span>
                <span className="text-red-600 dark:text-red-400 font-bold">{formatPrice(data.fiftyTwoWeekLow)}</span>
                <span className="text-gray-500 dark:text-gray-400 mx-2">-</span>
                <span className="text-green-700 dark:text-green-400 font-bold">{formatPrice(data.fiftyTwoWeekHigh)}</span>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Comprehensive Sentiment Analysis */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-600">
        <SentimentAggregatorChip ticker={data.symbol} timeframe="all" />
      </div>

      
    </div>
  );
};

export default StockHeader;